import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GalleryAsset } from '@xphoto/shared';
import { ApiError, fetchAssetPage, type GalleryFilters } from './api';
import { PAGE_SIZE } from './config';
import { planPages } from './page-planner';

/**
 * Chargement fenêtré des assets d'une bibliothèque pour la grille virtualisée.
 *
 * Implémentation MAISON (pas de react-query) : dépendance minimale, la logique
 * tient en un hook. On maintient un tableau creux (`sparse`) indexé par position
 * absolue ; la grille demande de garantir une plage (`ensureRange`) et le hook
 * charge les pages manquantes CONTIGUËMENT depuis la frontière chargée (le layout
 * justifié ne dispose que le préfixe dense — cf. `page-planner.ts`). `total`
 * (fourni par l'API) dimensionne le scroller virtualisé même avant que toutes les
 * pages soient chargées.
 *
 * Correctif « lignes vides au-delà de ~100 » (XPHOTO-18) : avant, la grille ne
 * demandait JAMAIS les pages suivantes de façon exploitable (la plage demandée
 * dégénérait en `[loadedBoundary, total]` → rafale de 135 pages → 429 en cascade,
 * puis blocage). Désormais on planifie des pages CONTIGUËS, dédoublonnées et
 * plafonnées par tick (anti-rafale), au fil du défilement → toute la bibliothèque
 * devient atteignable.
 *
 * Le changement de `libraryId`/`filters` réinitialise entièrement l'état
 * (nouvelle requête) et annule les chargements en vol.
 */

export type AssetsStatus = 'idle' | 'loading' | 'error' | 'ready';

/** Nature d'une erreur de chargement (pilote un message clair, cf. `GalleryBody`). */
export type AssetsErrorKind = 'rateLimited' | 'network' | 'server' | 'unknown';

/** Repli quand un 429 n'expose pas d'en-tête `Retry-After`. */
const DEFAULT_RETRY_AFTER_SECONDS = 5;

/**
 * Nombre d'auto-réessais d'une page ≥ 2 avant de rendre la main au bouton manuel.
 * Borné pour ne jamais boucler contre un serveur qui refuse durablement.
 */
const MAX_PAGE_AUTO_RETRIES = 3;

/** Plancher du délai d'auto-réessai d'une page (évite une boucle serrée). */
const MIN_PAGE_RETRY_MS = 2000;

/**
 * Échec de chargement d'une page AU-DELÀ de la première (audit F6).
 *
 * Avant, tout le traitement d'erreur de `loadPage` vivait dans `if (isFirst)` : un
 * échec sur la page 5 pendant le défilement ne posait AUCUN état. Les rangées
 * concernées restaient des SKELETONS PULSANTS — une fausse animation de chargement
 * indéfinie dès que l'utilisateur arrêtait de faire défiler (seul un nouveau
 * `ensureRange` pouvait re-déclencher la tentative).
 */
export interface PageError {
  /** Nature de l'échec (même vocabulaire que la première page). */
  kind: AssetsErrorKind;
  /** Un auto-réessai est encore planifié (sinon : rendre la main à l'utilisateur). */
  autoRetrying: boolean;
}

export interface UseAssetsResult {
  /** Total d'assets correspondant au filtre (dimensionne le scroller). */
  total: number;
  /** Nombre d'assets CONTIGUS chargés depuis l'offset 0 (frontière du préfixe dense). */
  loadedCount: number;
  /** Statut de la PREMIÈRE page (pilote skeleton / erreur / vide). */
  status: AssetsStatus;
  /** Code d'erreur normalisé si `status === 'error'`. */
  errorCode: string | null;
  /** Nature de l'erreur (429 rate-limit / réseau / 5xx / autre) si `status === 'error'`. */
  errorKind: AssetsErrorKind | null;
  /** Secondes suggérées avant nouvel essai (429 + `Retry-After`), sinon `null`. */
  retryAfter: number | null;
  /**
   * Échec d'une ou plusieurs pages AU-DELÀ de la première, ou `null`. Non bloquant :
   * la grille reste affichée et utilisable, seul un bandeau signale le trou.
   */
  pageError: PageError | null;
  /** Re-tente immédiatement TOUTES les pages en échec (bouton « Réessayer » du bandeau). */
  retryFailedPages: () => void;
  /** Accès à un asset chargé (ou `undefined` si sa page n'est pas encore là). */
  getAsset: (index: number) => GalleryAsset | undefined;
  /**
   * Garantit le chargement jusqu'à l'index absolu `targetEnd` (exclusif) — la fin de
   * la fenêtre visible + un tampon d'anticipation. Charge des pages CONTIGUËS depuis
   * la frontière chargée, plafonnées par tick (anti-rafale). `start` (début visible)
   * est accepté pour la signature historique mais n'est pas requis (chargement
   * séquentiel depuis 0).
   */
  ensureRange: (start: number, end: number) => void;
  /** Relance la requête courante (bouton « réessayer »). */
  reload: () => void;
  /** Mutation locale d'un asset chargé (ex. état changé depuis la visionneuse). */
  patchAsset: (index: number, patch: Partial<GalleryAsset>) => void;
  /** Index absolu d'un id parmi les assets CHARGÉS, ou `-1` (deep-link visionneuse). */
  indexOfLoaded: (id: string) => number;
}

/**
 * Avance la frontière du préfixe dense (nombre d'assets contigus depuis 0) À PARTIR de
 * sa valeur courante, et rend la nouvelle valeur.
 *
 * ⚠ PERF (audit B2) : la version précédente repartait de 0 à chaque appel — et elle est
 * appelée depuis `ensureRange`, donc à CHAQUE tick de défilement. Sur une bibliothèque de
 * 50 000 assets entièrement parcourue, chaque tick coûtait 50 000 itérations pour
 * re-découvrir une frontière qui n'avait pas bougé. Maintenue de façon incrémentale, elle
 * est amortie O(1) : chaque case n'est visitée qu'une fois sur toute la vie du tableau.
 *
 * ⚠ La frontière DOIT être remise à 0 en même temps que le tableau creux (changement de
 * filtre / `reload`), sinon elle resterait « en avance » sur un tableau vide.
 */
function advanceContiguousLength(arr: (GalleryAsset | undefined)[], from: number): number {
  let n = Math.max(0, from);
  while (arr[n] !== undefined) n += 1;
  return n;
}

export function useAssets(libraryId: string, filters: GalleryFilters): UseAssetsResult {
  const [total, setTotal] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [status, setStatus] = useState<AssetsStatus>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<AssetsErrorKind | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [pageError, setPageError] = useState<PageError | null>(null);
  // Version bumpée à chaque reset (lib/filtres/reload) pour forcer le re-render
  // quand le tableau creux (mutable, dans une ref) se remplit.
  const [, setVersion] = useState(0);

  const itemsRef = useRef<(GalleryAsset | undefined)[]>([]);
  // Frontière du préfixe dense, maintenue INCRÉMENTALEMENT (cf. `advanceContiguousLength`).
  // Miroir en `ref` de l'état `loadedCount`, pour une lecture synchrone depuis
  // `ensureRange` (callback mémoïsé : l'état y serait périmé).
  const loadedCountRef = useRef(0);
  const requestedPagesRef = useRef<Set<number>>(new Set());
  const controllersRef = useRef<Set<AbortController>>(new Set());
  // Total connu, lu de façon synchrone par `ensureRange` (évite une dépendance d'état
  // périmée dans les callbacks mémoïsés → planification toujours à jour).
  const totalRef = useRef(0);
  // Filtres lus via une ref : `loadPage` NE DOIT PAS dépendre de l'objet `filters`
  // (souvent recréé à chaque render par l'appelant) sous peine de boucle de rendu
  // (effect → reset → render → nouveau loadPage → effect…). La dépendance stable est
  // `filtersKey` (chaîne sérialisée) ; la ref fournit la valeur courante au fetch.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Pages ≥ 2 en échec : `offset -> nombre de tentatives auto déjà consommées`.
  const failedPagesRef = useRef<Map<number, number>>(new Map());
  // Auto-réessai des pages en échec (une seule salve armée à la fois).
  const pageRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `loadPage` par référence : la planification d'un réessai doit pouvoir l'appeler
  // sans créer de cycle de dépendances entre callbacks mémoïsés.
  const loadPageRef = useRef<(offset: number) => void>(() => {});

  // Sérialise les filtres pour une dépendance stable (évite les resets inutiles).
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  /** Coupe l'auto-réessai de pages en vol (reset, réessai manuel, démontage). */
  const clearPageRetry = useCallback(() => {
    if (pageRetryTimerRef.current !== null) {
      clearTimeout(pageRetryTimerRef.current);
      pageRetryTimerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    for (const c of controllersRef.current) c.abort();
    controllersRef.current = new Set();
    itemsRef.current = [];
    // Tableau creux vidé ⇒ la frontière incrémentale DOIT repartir de zéro (audit B2).
    loadedCountRef.current = 0;
    requestedPagesRef.current = new Set();
    failedPagesRef.current = new Map();
    clearPageRetry();
    totalRef.current = 0;
    setTotal(0);
    setLoadedCount(0);
    setErrorCode(null);
    setErrorKind(null);
    setRetryAfter(null);
    setPageError(null);
    setStatus('idle');
    setVersion((v) => v + 1);
  }, [clearPageRetry]);

  /**
   * Planifie UNE salve d'auto-réessai des pages en échec, après `delayMs` (piloté par
   * `Retry-After` sur un 429, planché). Chaque offset a son compteur propre : passé
   * `MAX_PAGE_AUTO_RETRIES`, il n'est plus re-tenté automatiquement et le bandeau rend
   * la main au bouton « Réessayer » manuel.
   */
  const schedulePageRetry = useCallback((delayMs: number) => {
    if (pageRetryTimerRef.current !== null) return; // salve déjà armée
    pageRetryTimerRef.current = setTimeout(
      () => {
        pageRetryTimerRef.current = null;
        const retryable = [...failedPagesRef.current.entries()]
          .filter(([, attempts]) => attempts < MAX_PAGE_AUTO_RETRIES)
          .map(([offset]) => offset);
        if (retryable.length === 0) {
          // Plus rien d'automatique : le bandeau bascule en « réessai manuel ».
          setPageError((prev) =>
            prev && prev.autoRetrying ? { ...prev, autoRetrying: false } : prev,
          );
          return;
        }
        for (const offset of retryable) {
          failedPagesRef.current.set(offset, (failedPagesRef.current.get(offset) ?? 0) + 1);
          loadPageRef.current(offset);
        }
      },
      Math.max(MIN_PAGE_RETRY_MS, delayMs),
    );
  }, []);

  const loadPage = useCallback(
    (offset: number) => {
      if (!libraryId) return;
      if (requestedPagesRef.current.has(offset)) return;
      requestedPagesRef.current.add(offset);

      const isFirst = offset === 0;
      if (isFirst) setStatus('loading');

      const controller = new AbortController();
      controllersRef.current.add(controller);

      fetchAssetPage(libraryId, filtersRef.current, offset, PAGE_SIZE, controller.signal)
        .then((page) => {
          if (controller.signal.aborted) return;
          totalRef.current = page.total;
          setTotal(page.total);
          const arr = itemsRef.current;
          for (let i = 0; i < page.items.length; i += 1) {
            arr[offset + i] = page.items[i];
          }
          loadedCountRef.current = advanceContiguousLength(arr, loadedCountRef.current);
          setLoadedCount(loadedCountRef.current);
          if (isFirst) setStatus('ready');
          // Page rétablie : elle sort de la liste des échecs ; le bandeau disparaît
          // dès qu'il ne reste plus aucun trou.
          if (failedPagesRef.current.delete(offset) && failedPagesRef.current.size === 0) {
            setPageError(null);
          }
          setVersion((v) => v + 1);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          // La page échouée pourra être re-tentée.
          requestedPagesRef.current.delete(offset);
          // Nature de l'erreur, commune aux deux chemins (première page et suivantes) :
          // le vocabulaire des messages reste STRICTEMENT le même.
          let kind: AssetsErrorKind;
          let retrySeconds: number | null = null;
          if (err instanceof ApiError) {
            if (err.status === 429) {
              kind = 'rateLimited';
              retrySeconds = err.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS;
            } else {
              kind = err.status >= 500 ? 'server' : 'unknown';
            }
          } else {
            // Rejet hors HTTP (réseau coupé, DNS, CORS, expiration du plafond…).
            kind = 'network';
          }
          if (isFirst) {
            setErrorCode(err instanceof ApiError ? err.code : 'NETWORK');
            setErrorKind(kind);
            setRetryAfter(retrySeconds);
            setStatus('error');
            return;
          }
          // Page ≥ 2 : signal NON BLOQUANT (la grille reste utilisable) + auto-réessai
          // borné. Sans ça, les rangées concernées restaient des skeletons éternels.
          if (!failedPagesRef.current.has(offset)) failedPagesRef.current.set(offset, 0);
          const attempts = failedPagesRef.current.get(offset) ?? 0;
          const autoRetrying = attempts < MAX_PAGE_AUTO_RETRIES;
          setPageError({ kind, autoRetrying });
          if (autoRetrying) {
            schedulePageRetry((retrySeconds ?? DEFAULT_RETRY_AFTER_SECONDS) * 1000);
          }
        })
        .finally(() => {
          controllersRef.current.delete(controller);
        });
    },
    // `filtersKey` (chaîne stable) plutôt que `filters` (objet volatil) : garde
    // `loadPage` stable tant que la requête ne change pas → pas de boucle de rendu.
    [libraryId, filtersKey, schedulePageRetry],
  );

  // Expose `loadPage` par référence pour l'auto-réessai (voir `schedulePageRetry`).
  loadPageRef.current = loadPage;

  // Reset + amorçage de la première page à chaque nouvelle requête.
  useEffect(() => {
    reset();
    loadPage(0);
    return () => {
      for (const c of controllersRef.current) c.abort();
      controllersRef.current = new Set();
      clearPageRetry();
    };
    // Dépendances volontaires : `filtersKey` capture `filters` (stringifié) ;
    // `loadPage`/`reset` sont stables pour une requête donnée.
  }, [libraryId, filtersKey, loadPage, reset, clearPageRetry]);

  /**
   * Réessai MANUEL des pages en échec (bouton du bandeau) : remet les compteurs
   * d'auto-réessai à zéro (l'utilisateur redonne sa chance au serveur) et relance
   * tout de suite, sans attendre le prochain `ensureRange`.
   */
  const retryFailedPages = useCallback(() => {
    clearPageRetry();
    const offsets = [...failedPagesRef.current.keys()];
    if (offsets.length === 0) {
      setPageError(null);
      return;
    }
    setPageError((prev) => (prev ? { ...prev, autoRetrying: true } : prev));
    for (const offset of offsets) {
      failedPagesRef.current.set(offset, 0);
      loadPage(offset);
    }
  }, [clearPageRetry, loadPage]);

  const ensureRange = useCallback(
    (_start: number, end: number) => {
      const currentTotal = totalRef.current;
      if (currentTotal <= 0) return;
      const offsets = planPages({
        loadedCount: loadedCountRef.current,
        targetEnd: Math.max(0, end),
        total: currentTotal,
        requested: requestedPagesRef.current,
      });
      for (const offset of offsets) loadPage(offset);
    },
    [loadPage],
  );

  const getAsset = useCallback((index: number): GalleryAsset | undefined => {
    return itemsRef.current[index];
  }, []);

  const reload = useCallback(() => {
    reset();
    loadPage(0);
  }, [reset, loadPage]);

  const patchAsset = useCallback((index: number, patch: Partial<GalleryAsset>) => {
    const current = itemsRef.current[index];
    if (!current) return;
    itemsRef.current[index] = { ...current, ...patch };
    setVersion((v) => v + 1);
  }, []);

  // Recherche linéaire dans le tableau creux des assets chargés (l'`id` d'un voisin
  // sert d'ancre de navigation pour la route `/photo/:id`). `-1` si pas (encore) là.
  const indexOfLoaded = useCallback((id: string): number => {
    const arr = itemsRef.current;
    for (let i = 0; i < arr.length; i += 1) {
      if (arr[i]?.id === id) return i;
    }
    return -1;
  }, []);

  return {
    total,
    loadedCount,
    status,
    errorCode,
    errorKind,
    retryAfter,
    pageError,
    retryFailedPages,
    getAsset,
    ensureRange,
    reload,
    patchAsset,
    indexOfLoaded,
  };
}
